import Button from '@/common/components/atom/button/button';
import Modal from '@/common/components/atom/modal';
import Text from '@/common/components/atom/text/text';
import WifiIcon from '@/common/components/icons/wifi';
import { useEffect, useRef, useState } from 'react';

const fetchIsIran = async (): Promise<boolean | null> => {
  try {
    // Called from the browser so the gateway sees the user's own (VPN) IP.
    const res = await fetch('https://apigw.paziresh24.com/ipwho/', { cache: 'no-store' });
    const data = await res.json();
    return data?.success && data?.country_code ? data.country_code === 'IR' : null;
  } catch {
    return null;
  }
};

/**
 * Bank gateways reject non-Iranian IPs, so a foreign IP at checkout means the VPN is
 * almost certainly on. Right before going to the gateway, show a one-time hint asking
 * to turn it off. Unknown region (lookup failed or slow) never blocks payment.
 *
 * `guard(proceed)` resolves after `proceed` ran, or without running it when the user
 * dismissed the sheet (so callers' pending/loading state can always be cleared).
 */
export const useVpnPaymentHint = () => {
  const [open, setOpen] = useState(false);
  const isIran = useRef<boolean | null>(null);
  const shown = useRef(false);
  const pending = useRef<{ proceed: () => unknown; resolve: () => void } | null>(null);

  useEffect(() => {
    fetchIsIran().then(v => (isIran.current = v));
  }, []);

  // Never waits on the lookup: if it hasn't answered yet, the click proceeds immediately.
  const guard = async (proceed: () => unknown) => {
    if (isIran.current !== false || shown.current) return void (await proceed());
    shown.current = true;
    return new Promise<void>(resolve => {
      pending.current = { proceed, resolve };
      setOpen(true);
    });
  };

  const settle = async (run: boolean) => {
    setOpen(false);
    const p = pending.current;
    pending.current = null;
    if (!p) return;
    try {
      if (run) await p.proceed();
    } finally {
      p.resolve();
    }
  };

  const sheet = (
    <Modal
      name="vpn-hint"
      bindHistory={false}
      isOpen={open}
      onClose={() => settle(false)}
      title="فیلترشکن خود را خاموش کنید"
      noHeader
      bodyClassName="!px-5 !pt-6 !pb-5"
    >
      <div className="flex flex-col items-center gap-5 text-center">
        <span className="flex items-center justify-center rounded-full w-16 h-16 bg-amber-50 text-amber-600">
          <WifiIcon width={32} height={32} />
        </span>
        <div className="flex flex-col gap-2">
          <Text fontWeight="bold" className="text-lg text-slate-900">
            فیلترشکن روشن است
          </Text>
          <Text fontSize="sm" className="text-slate-500">
            درگاه‌های بانکی با فیلترشکن باز نمی‌شوند.
          </Text>
        </div>
        <ol className="flex flex-col gap-3 w-full p-4 rounded-lg bg-slate-50 text-start">
          {['فیلترشکن را خاموش کنید', 'روی «ادامهٔ پرداخت» بزنید'].map((step, i) => (
            <li key={step} className="flex items-center gap-3">
              <span className="flex shrink-0 items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold">
                {(i + 1).toLocaleString('fa-IR')}
              </span>
              <Text fontSize="sm" className="text-slate-700">
                {step}
              </Text>
            </li>
          ))}
        </ol>
        <Button block onClick={() => settle(true)}>
          خاموش کردم، ادامهٔ پرداخت
        </Button>
      </div>
    </Modal>
  );

  return { guard, sheet };
};
